import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, MapPin, CreditCard, Bell, Shield } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { isAuthenticated, getCurrentUser } from '../lib/authUtils';

const Account = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const currentUser = getCurrentUser();
  
  const [profileData, setProfileData] = useState({
    firstName: currentUser?.firstName || '',
    lastName: currentUser?.lastName || '',
    email: currentUser?.email || '',
    phone: currentUser?.phone || '',
    company: currentUser?.company || '',
    taxId: currentUser?.taxId || ''
  });

  // Check authentication on mount
  useEffect(() => {
    if (!isAuthenticated()) {
      toast({
        title: 'Login Required',
        description: 'Please login to access your account.',
        variant: 'destructive',
      });
      navigate('/customer-login', { state: { from: { pathname: '/account' } } });
    }
  }, [navigate]);

  // Don't render if not authenticated
  if (!isAuthenticated()) {
    return null;
  }

  const handleSaveProfile = (e) => {
    e.preventDefault();
    toast({
      title: 'Profile Updated',
      description: 'Your profile has been successfully updated.'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">My Account</h1>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow">
              <nav className="flex flex-col">
                <button
                  onClick={() => setActiveTab('profile')}
                  className={`flex items-center space-x-3 px-6 py-4 border-b hover:bg-gray-50 transition ${
                    activeTab === 'profile' ? 'bg-red-50 text-red-600 border-l-4 border-red-600' : 'text-gray-700'
                  }`}
                >
                  <User size={20} />
                  <span className="font-semibold">Profile</span>
                </button>
                <button
                  onClick={() => setActiveTab('addresses')}
                  className={`flex items-center space-x-3 px-6 py-4 border-b hover:bg-gray-50 transition ${
                    activeTab === 'addresses' ? 'bg-red-50 text-red-600 border-l-4 border-red-600' : 'text-gray-700'
                  }`}
                >
                  <MapPin size={20} />
                  <span className="font-semibold">Addresses</span>
                </button>
                <button
                  onClick={() => setActiveTab('payment')}
                  className={`flex items-center space-x-3 px-6 py-4 border-b hover:bg-gray-50 transition ${
                    activeTab === 'payment' ? 'bg-red-50 text-red-600 border-l-4 border-red-600' : 'text-gray-700'
                  }`}
                >
                  <CreditCard size={20} />
                  <span className="font-semibold">Payment Methods</span>
                </button>
                <button
                  onClick={() => setActiveTab('notifications')}
                  className={`flex items-center space-x-3 px-6 py-4 border-b hover:bg-gray-50 transition ${
                    activeTab === 'notifications' ? 'bg-red-50 text-red-600 border-l-4 border-red-600' : 'text-gray-700'
                  }`}
                >
                  <Bell size={20} />
                  <span className="font-semibold">Notifications</span>
                </button>
                <button
                  onClick={() => setActiveTab('security')}
                  className={`flex items-center space-x-3 px-6 py-4 hover:bg-gray-50 transition ${
                    activeTab === 'security' ? 'bg-red-50 text-red-600 border-l-4 border-red-600' : 'text-gray-700'
                  }`}
                >
                  <Shield size={20} />
                  <span className="font-semibold">Security</span>
                </button>
              </nav>
            </div>
          </aside>

          {/* Main Content */}
          <main className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow p-6">
              {/* Profile Tab */}
              {activeTab === 'profile' && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Profile Information</h2>
                  <form onSubmit={handleSaveProfile}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          First Name
                        </label>
                        <input
                          type="text"
                          value={profileData.firstName}
                          onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-red-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Last Name
                        </label>
                        <input
                          type="text"
                          value={profileData.lastName}
                          onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-red-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Email
                        </label>
                        <input
                          type="email"
                          value={profileData.email}
                          onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-red-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Phone
                        </label>
                        <input
                          type="tel"
                          value={profileData.phone}
                          onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-red-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Company Name
                        </label>
                        <input
                          type="text"
                          value={profileData.company}
                          onChange={(e) => setProfileData({ ...profileData, company: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-red-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Tax ID
                        </label>
                        <input
                          type="text"
                          value={profileData.taxId}
                          onChange={(e) => setProfileData({ ...profileData, taxId: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-red-500"
                        />
                      </div>
                    </div>
                    <button
                      type="submit"
                      className="mt-6 bg-red-600 text-white px-8 py-3 rounded hover:bg-red-700 transition font-semibold"
                    >
                      Save Changes
                    </button>
                  </form>
                </div>
              )}

              {/* Addresses Tab */}
              {activeTab === 'addresses' && (
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">Saved Addresses</h2>
                    <button className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition font-semibold">
                      Add New Address
                    </button>
                  </div>
                  <div className="space-y-4">
                    <div className="border rounded-lg p-4 hover:border-red-500 transition">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold text-gray-900 mb-1">Home</p>
                          <p className="text-gray-600 text-sm">
                            123 Industrial Way<br />
                            Factory District<br />
                            New York, NY 10001
                          </p>
                        </div>
                        <div className="flex space-x-2">
                          <button className="text-blue-600 hover:text-blue-700 text-sm font-semibold">
                            Edit
                          </button>
                          <button className="text-red-600 hover:text-red-700 text-sm font-semibold">
                            Delete
                          </button>
                        </div>
                      </div>
                      <span className="inline-block mt-2 bg-green-100 text-green-800 text-xs px-2 py-1 rounded font-semibold">
                        Default
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Payment Methods Tab */}
              {activeTab === 'payment' && (
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">Payment Methods</h2>
                    <button className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition font-semibold">
                      Add Payment Method
                    </button>
                  </div>
                  <div className="space-y-4">
                    <div className="border rounded-lg p-4 hover:border-red-500 transition">
                      <div className="flex justify-between items-start">
                        <div className="flex items-center space-x-4">
                          <div className="bg-gray-100 rounded p-2">
                            <CreditCard size={24} className="text-gray-600" />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">Visa ending in 4242</p>
                            <p className="text-gray-600 text-sm">Expires 12/25</p>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <button className="text-blue-600 hover:text-blue-700 text-sm font-semibold">
                            Edit
                          </button>
                          <button className="text-red-600 hover:text-red-700 text-sm font-semibold">
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Notifications Tab */}
              {activeTab === 'notifications' && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Notification Preferences</h2>
                  <div className="space-y-4">
                    <label className="flex items-center justify-between p-4 border rounded hover:bg-gray-50 cursor-pointer">
                      <div>
                        <p className="font-semibold text-gray-900">Order Updates</p>
                        <p className="text-sm text-gray-600">Get notified about order status changes</p>
                      </div>
                      <input type="checkbox" defaultChecked className="accent-red-600" />
                    </label>
                    <label className="flex items-center justify-between p-4 border rounded hover:bg-gray-50 cursor-pointer">
                      <div>
                        <p className="font-semibold text-gray-900">Quote Responses</p>
                        <p className="text-sm text-gray-600">Receive notifications for quote approvals</p>
                      </div>
                      <input type="checkbox" defaultChecked className="accent-red-600" />
                    </label>
                    <label className="flex items-center justify-between p-4 border rounded hover:bg-gray-50 cursor-pointer">
                      <div>
                        <p className="font-semibold text-gray-900">Promotional Emails</p>
                        <p className="text-sm text-gray-600">Receive special offers and discounts</p>
                      </div>
                      <input type="checkbox" className="accent-red-600" />
                    </label>
                    <label className="flex items-center justify-between p-4 border rounded hover:bg-gray-50 cursor-pointer">
                      <div>
                        <p className="font-semibold text-gray-900">Stock Alerts</p>
                        <p className="text-sm text-gray-600">Alert when wishlist items are back in stock</p>
                      </div>
                      <input type="checkbox" defaultChecked className="accent-red-600" />
                    </label>
                  </div>
                  <button className="mt-6 bg-red-600 text-white px-8 py-3 rounded hover:bg-red-700 transition font-semibold">
                    Save Preferences
                  </button>
                </div>
              )}

              {/* Security Tab */}
              {activeTab === 'security' && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Security Settings</h2>
                  <div className="space-y-6">
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-4">Change Password</h3>
                      <form className="space-y-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Current Password
                          </label>
                          <input
                            type="password"
                            className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-red-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            New Password
                          </label>
                          <input
                            type="password"
                            className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-red-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Confirm New Password
                          </label>
                          <input
                            type="password"
                            className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-red-500"
                          />
                        </div>
                        <button
                          type="submit"
                          className="bg-red-600 text-white px-6 py-2 rounded hover:bg-red-700 transition font-semibold"
                        >
                          Update Password
                        </button>
                      </form>
                    </div>

                    <div className="border-t pt-6">
                      <h3 className="font-semibold text-gray-900 mb-4">Two-Factor Authentication</h3>
                      <p className="text-gray-600 mb-4">Add an extra layer of security to your account</p>
                      <button className="border border-gray-300 px-6 py-2 rounded hover:bg-gray-50 transition font-semibold">
                        Enable 2FA
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default Account;
